import store from '@/store/index';
/**
 * Is responsible for redrawing the map based on store data,
 * one can reason about it as a single point of entry map manipuplation class
 */
import MapService from '@/services/MapService';
import { LayerGroup } from 'leaflet';

export default class RedrawService {
  private mapService: MapService;

  constructor(mapService: MapService) {
    this.mapService = mapService;
  }

  private parkingSpotsBreakpointZoomFactor: number = 20;
  private zoneMarkerBreakpointZoomFactor: number = 15;
  private zoneMarkerVisibleGroup: any = {
    from: 17,
    to: 19,
  };
  private zoomMarkerVisibleMeters: number = 17;


  /**
   * Redraws everything on the map.
   */
  public redrawMap = (): void => {
    const { mapConfiguration } = store.state.mapSettings;

    this.removeLayersBeforeRedraw();
    if (mapConfiguration.parkings) {
      this.redrawParkings();
    }
    if (mapConfiguration.parkingZone) {
      this.redrawParkingZones();
    }
    if (mapConfiguration.parkingMeters) {
      this.redrawParkingMeters();
    }
    if (mapConfiguration.parkings && mapConfiguration.parkingSpots) {
      this.redrawParkingSpots();
    }
    if (mapConfiguration.parkings && mapConfiguration.parkingGroups) {
      this.redrawParkingGroups();
    }
    if (mapConfiguration.events && mapConfiguration.accident) {
      this.redrawAccidentsEvents();
    }
    if (mapConfiguration.events && mapConfiguration.constructions) {
      this.redrawBuildingEvents();
    }
    if (mapConfiguration.events && mapConfiguration.roadClosed) {
      this.redrawBlocksEvents();
    }
    if (mapConfiguration.events && mapConfiguration.hazard) {
      this.redrawHazardEvents();
    }
    if (mapConfiguration.weatherStations) {
      this.redrawWeatherStations();
    }
  };

  /**
   * Removes layers from the map.
   */
  private removeLayersBeforeRedraw = (): void => {
    const { leafletLayers } = store.state;

    Object.values(leafletLayers).map((o: LayerGroup) => o.remove());
  };

  /**
   * Redraws parking spots on the map.
   * zoom 0 do 15 - parkingi
   */
  private redrawParkings = (): void => {
    const oldLayer = store.state.leafletLayers.parkingLayer;
    if (store.state.zoomFactor < this.zoneMarkerVisibleGroup.from) {
      try {
        store.commit('updateParkingsLayerGroup', this.mapService.drawParkingsMarkers());
      } catch (e) {
        console.error('Redraw failed! - parkings couldn\'t been drawn'); // eslint-disable-line no-console
        console.error(e); // eslint-disable-line no-console
      }
    }
    oldLayer.remove();
  };

  /**
   * Redraws parking zones on the map.
   */
  private redrawParkingZones = (): void => {
    const oldParkingZonesLayer = store.state.leafletLayers.parkingZones;
    const oldParkingZonesMarkersLayer = store.state.leafletLayers.parkingZonesMarkers;
    if (store.state.zoomFactor >= this.zoneMarkerBreakpointZoomFactor) {
      try {
        store.commit('updateParkingZonesLayerGroup', this.mapService.drawParkingZonesPolygons());
      } catch (e) {
        console.error('Redraw failed! - parking zones couldn\'t been drawn'); // eslint-disable-line no-console
        console.error(e); // eslint-disable-line no-console
      }
    } else {
      try {
        store.commit('updateParkingZonesMarkersLayerGroup', this.mapService.drawParkingZonesMarkers());
      } catch (e) {
        console.error('Redraw failed! - parking zones markers couldn\'t been drawn'); // eslint-disable-line no-console
        console.error(e); // eslint-disable-line no-console
      }
    }
    oldParkingZonesLayer.remove();
    oldParkingZonesMarkersLayer.remove();
  };

  /**
   * Redraws parking meters on the map.
   */
  private redrawParkingMeters = (): void => {
    const oldLayer = store.state.leafletLayers.parkingMeters;
    if (store.state.zoomFactor >= this.zoomMarkerVisibleMeters) {
      try {
        store.commit('updateParkingMetersLayerGroup', this.mapService.drawParkingMetersMarkers());
      } catch (e) {
        console.error('Redraw failed! - parking meters couldn\'t been drawn'); // eslint-disable-line no-console
        console.error(e); // eslint-disable-line no-console
      }
    }
    oldLayer.remove();
  };

  /**
   * Redraws parking spots on the map.
   * zoom 19 + - miejsca parkingowe
   */
  private redrawParkingSpots = ():void => {
    const oldLayer = store.state.leafletLayers.parkingSpots;
    if (store.state.zoomFactor >= this.parkingSpotsBreakpointZoomFactor) {
      try {
        store.commit('updateParkingSpotsLayerGroup', this.mapService.drawParkingSpotsMarkers());
      } catch (e) {
        console.error('Redraw failed! - parking spots couldn\'t been drawn'); // eslint-disable-line no-console
        console.error(e); // eslint-disable-line no-console
      }
    }
    oldLayer.remove();
  };

  /**
   * Redraws parking group on the map.
   * zoom 16 do 18 - grupy parkingow (kreski)
   */
  private redrawParkingGroups = ():void => {
    const oldLayer = store.state.leafletLayers.parkingGroups;
    if (store.state.zoomFactor >= this.zoneMarkerVisibleGroup.from && store.state.zoomFactor <= this.zoneMarkerVisibleGroup.to) {
      try {
        store.commit('updateParkingGroupsLayerGroup', this.mapService.drawParkingGroupsPolygons());
      } catch (e) {
        console.error('Redraw failed! - parking groups couldn\'t been drawn'); // eslint-disable-line no-console
        console.error(e); // eslint-disable-line no-console
      }
    }
    oldLayer.remove();
  };

  /**
   * Redraws parking EVENTS - accidents on the map.
   */
  private redrawAccidentsEvents = ():void => {
    const oldLayer = store.state.leafletLayers.eventsAccidents;
    try {
      store.commit('updateEventsAccidentsLayerGroup', this.mapService.drawEventsMarkers('ACCIDENT'));
    } catch (e) {
      console.error('Redraw failed! - accident events couldn\'t been drawn'); // eslint-disable-line no-console
      console.error(e); // eslint-disable-line no-console
    }
    oldLayer.remove();
  };

  /**
   * Redraws parking EVENTS - building on the map.
   */
  private redrawBuildingEvents = ():void => {
    const oldLayer = store.state.leafletLayers.eventsBuilding;
    try {
      store.commit('updateEventsBuildingLayerGroup', this.mapService.drawEventsMarkers('CONSTRUCTION'));
    } catch (e) {
      console.error('Redraw failed! - construction events couldn\'t been drawn'); // eslint-disable-line no-console
      console.error(e); // eslint-disable-line no-console
    }
    oldLayer.remove();
  };

  /**
   * Redraws parking EVENTS group on the map.
   */
  private redrawBlocksEvents = ():void => {
    const oldLayer = store.state.leafletLayers.eventsBlocks;
    try {
      store.commit('updateEventsBlocksLayerGroup', this.mapService.drawEventsMarkers('ROAD'));
    } catch (e) {
      console.error('Redraw failed! - road events couldn\'t been drawn'); // eslint-disable-line no-console
      console.error(e); // eslint-disable-line no-console
    }
    oldLayer.remove();
  };

  /**
   * Redraws parking EVENTS - hazard group on the map.
   */
  private redrawHazardEvents = ():void => {
    const oldLayer = store.state.leafletLayers.eventsHazard;
    try {
      store.commit('updateEventsHazardLayerGroup', this.mapService.drawEventsMarkers('HAZARD'));
    } catch (e) {
      console.error('Redraw failed! - hazard events couldn\'t been drawn'); // eslint-disable-line no-console
      console.error(e); // eslint-disable-line no-console
    }
    oldLayer.remove();
  };

  /**
   * Redraws parking WEATHER group on the map.
   */
  private redrawWeatherStations = ():void => {
    const oldLayer = store.state.leafletLayers.weatherStations;
    try {
      store.commit(
        'updateWeatherStationsLayerGroup',
        this.mapService.drawWeatherStationsMarkers(),
      );
    } catch (e) {
      console.error('Redraw failed! - weather stations couldn\'t been drawn'); // eslint-disable-line no-console
      console.error(e); // eslint-disable-line no-console
    }
    oldLayer.remove();
  };

  /**
   * fetches data from the api and redraws the map
   */
  public updateAndRedraw = (): Promise<unknown> => new Promise<unknown>((resolve, reject) => {
    Promise.all([
      store.dispatch('updateParkings'),
      store.dispatch('updateParkingZones'),
      // store.dispatch('updateParkingMeters'),
      store.dispatch('updateParkingSpots'),
      store.dispatch('updateParkingGroups'),
      store.dispatch('updateEvents'),
      store.dispatch('updateCurrentWeather'),
      store.dispatch('updateWeatherStations'),
      store.dispatch('updatePosts'),
    ])
      .then(() => {
        this.redrawMap();
        resolve(true);
      })
      .catch(reject);
  });
}
